import React, { lazy, Suspense } from 'react';

import objectFitImages from 'object-fit-images';
import picturefill from 'picturefill';
import registerReact from 'utils/registerReact';

/**
 * React components
 */
import MainNav from 'components/MainNav/MainNav';
import Hint from 'components/Hint/Hint';
import Gallery from 'components/Gallery/Gallery';
import FullScreenGallery from 'components/FullScreenGallery/FullScreenGallery';
import PropertySlider from 'components/PropertySlider/PropertySlider';
import Newsletter from 'components/Newsletter/Newsletter';
import Arrow from 'components/Icons/Arrow/Arrow';
import SectionMenu from 'components/SectionMenu/SectionMenu';
import Properties from 'components/Properties/Properties';
import LightBox from 'components/LightBox/LightBox';
import StickyScrollHint from 'components/StickyScrollHint/StickyScrollHint';
import Staff from 'components/Staff/Staff';
import Spinner from 'components/Spinner/Spinner';
import DownloadReport from 'components/DownloadReport/DownloadReport';

const LocationMap = lazy(() =>
  import(
    'components/LocationMap/LocationMap.js' /* webpackChunkName: "LocationMap" */
  )
);

const ApplicationMap = lazy(() =>
  import(
    'components/ApplicationMap/ApplicationMap.js' /* webpackChunkName: "ApplicationMap" */
  )
);

const TravelTime = lazy(() =>
  import(
    'components/TravelTime/TravelTime.js' /* webpackChunkName: "TravelTime" */
  )
);

const RelatedArticles = lazy(() =>
  import(
    'components/RelatedArticles/RelatedArticles.js' /* webpackChunkName: "RelatedArticles" */
  )
);

const References = lazy(() =>
  import(
    'components/References/References.js' /* webpackChunkName: "References" */
  )
);

const MarketGraph = lazy(() =>
  import(
    'components/MarketGraph/MarketGraph.js' /* webpackChunkName: "MarketGraph" */
  )
);

const BusinessGraph = lazy(() =>
  import(
    'components/BusinessGraph/BusinessGraph.js' /* webpackChunkName: "BusinessGraph" */
  )
);

const DynamicChart = lazy(() =>
  import(
    'components/DynamicChart/DynamicChart.js' /* webpackChunkName: "DynamicChart" */
  )
);

const PropertyContactForm = lazy(() =>
  import(
    'components/PropertyContactForm/PropertyContactForm.js' /* webpackChunkName: "PropertyContactForm" */
  )
);

const MarketMap = lazy(() =>
  import(
    'components/MarketMap/MarketMap.js' /* webpackChunkName: "MarketMap" */
  )
);

const Articles = lazy(() =>
  import('components/Articles/Articles.js' /* webpackChunkName: "Articles" */)
);

const Transactions = lazy(() =>
  import(
    'components/Transactions/Transactions.js' /* webpackChunkName: "Transactions" */
  )
);

const TransactionSlider = lazy(() =>
  import(
    'components/TransactionSlider/TransactionSlider.js' /* webpackChunkName: "TransactionSlider" */
  )
);

const Premises = lazy(() =>
  import('components/Premises/Premises.js' /* webpackChunkName: "Premises" */)
);

const Vimeo = lazy(() =>
  import('components/Vimeo/Vimeo.js' /* webpackChunkName: "Vimeo" */)
);

const MarketSummary = lazy(() =>
  import(
    'components/MarketSummary/MarketSummary.js' /* webpackChunkName: "MarketSummary" */
  )
);

const Screen = lazy(() =>
  import('components/Screen/Screen.js' /* webpackChunkName: "Screen" */)
);

const Counter = lazy(() =>
  import('components/Counter/Counter.js' /* webpackChunkName: "Counter" */)
);

const MarketFigures = lazy(() =>
  import(
    'components/MarketFigures/MarketFigures.js' /* webpackChunkName: "MarketFigures" */
  )
);

const SegmentDive = lazy(() =>
  import(
    'components/SegmentDive/SegmentDive.js' /* webpackChunkName: "SegmentDive" */
  )
);

const MarketArticles = lazy(() =>
  import(
    'components/MarketArticles/MarketArticles.js' /* webpackChunkName: "MarketArticles" */
  )
);

const PublicTransport = lazy(() =>
  import(
    'components/PublicTransport/PublicTransport.js' /* webpackChunkName: "PublicTransport" */
  )
);

const ShareApplication = lazy(() =>
  import(
    'components/ShareApplication/ShareApplication.js' /* webpackChunkName: "ShareApplication" */
  )
);

const ApplicationDetails = lazy(() =>
  import(
    'components/ApplicationDetails/ApplicationDetails.js' /* webpackChunkName: "ApplicationDetails" */
  )
);

const ApplicationGallery = lazy(() =>
  import(
    'components/ApplicationGallery/ApplicationGallery.js' /* webpackChunkName: "ApplicationGallery" */
  )
);

const PremisesLinks = lazy(() =>
  import(
    'components/PremisesLinks/PremisesLinks.js' /* webpackChunkName: "PremisesLinks" */
  )
);

export default () => {
  registerReact('main-nav-component', MainNav);
  registerReact('hint-component', Hint);
  registerReact('gallery-component', Gallery);
  registerReact('full-screen-gallery-component', FullScreenGallery);
  registerReact('property-slider-component', PropertySlider);
  registerReact('newsletter-component', Newsletter);
  registerReact('arrow', Arrow);
  registerReact('section-menu-component', SectionMenu);
  registerReact('properties-component', Properties);
  registerReact('light-box-component', LightBox);
  registerReact('sticky-scroll-hint-component', StickyScrollHint);
  registerReact('staff-component', Staff);

  registerReact('location-map-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <LocationMap {...props} />
    </Suspense>
  ));

  registerReact('application-map-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <ApplicationMap {...props} />
    </Suspense>
  ));

  registerReact('travel-time-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <TravelTime {...props} />
    </Suspense>
  ));

  registerReact('references-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <References {...props} />
    </Suspense>
  ));

  registerReact('articles-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <Articles {...props} />
    </Suspense>
  ));

  registerReact('market-map-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <MarketMap {...props} />
    </Suspense>
  ));
  registerReact('market-graph-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <MarketGraph {...props} />
    </Suspense>
  ));
  registerReact('business-graph-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <BusinessGraph {...props} />
    </Suspense>
  ));
  registerReact('dynamicchart-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <DynamicChart {...props} />
    </Suspense>
  ));
  registerReact('property-contact-form', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <PropertyContactForm {...props} />
    </Suspense>
  ));
  registerReact('transactions-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <Transactions {...props} />
    </Suspense>
  ));
  registerReact('transaction-slider-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <TransactionSlider {...props} />
    </Suspense>
  ));
  registerReact('premises-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <Premises {...props} />
    </Suspense>
  ));
  registerReact('related-articles-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <RelatedArticles {...props} />
    </Suspense>
  ));
  registerReact('vimeo-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <Vimeo {...props} />
    </Suspense>
  ));
  registerReact('market-summary-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <MarketSummary {...props} />
    </Suspense>
  ));

  registerReact('screen-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <Screen {...props} />
    </Suspense>
  ));

  registerReact('counter-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <Counter {...props} />
    </Suspense>
  ));

  registerReact('market-figures-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <MarketFigures {...props} />
    </Suspense>
  ));

  registerReact('segment-dive-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <SegmentDive {...props} />
    </Suspense>
  ));

  registerReact('download-report-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <DownloadReport {...props} />
    </Suspense>
  ));

  registerReact('market-articles-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <MarketArticles {...props} />
    </Suspense>
  ));

  registerReact('public-transport-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <PublicTransport {...props} />
    </Suspense>
  ));

  registerReact('share-application-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <ShareApplication {...props} />
    </Suspense>
  ));

  registerReact('application-details-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <ApplicationDetails {...props} />
    </Suspense>
  ));

  registerReact('application-gallery-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <ApplicationGallery {...props} />
    </Suspense>
  ));

  registerReact('premises-links-component', props => (
    <Suspense fallback={<Spinner className="suspense" />}>
      <PremisesLinks {...props} />
    </Suspense>
  ));

  picturefill();
  objectFitImages();
};
